import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;

interface AdminAccountSettingsProps {
  onToast: (msg: string, type?: 'success' | 'error') => void;
}

type EmailStep = 'idle' | 'enter_new' | 'confirm_sent' | 'enter_otp' | 'done';

export default function AdminAccountSettings({ onToast }: AdminAccountSettingsProps) {
  const [loading, setLoading] = useState(true);

  // Profile
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [nameSaved, setNameSaved] = useState(false);

  // Password change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordSaved, setPasswordSaved] = useState(false);

  // Email change
  const [emailStep, setEmailStep] = useState<EmailStep>('idle');
  const [newEmail, setNewEmail] = useState('');
  const [confirmNewEmail, setConfirmNewEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [emailCountdown, setEmailCountdown] = useState(0);

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (user) setAdminEmail(user.email ?? '');
      const { data } = await supabase.from('admin_settings').select('admin_name').eq('id', 1).maybeSingle();
      if (data) setAdminName(data.admin_name ?? '');
      setLoading(false);
    };
    load();
  }, []);

  // Countdown timer for resend
  useEffect(() => {
    if (emailCountdown <= 0) return;
    const t = setTimeout(() => setEmailCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [emailCountdown]);

  // ── Save Name ──
  const handleSaveName = async () => {
    if (!adminName.trim()) {
      setErrors(er => ({ ...er, adminName: 'يرجى إدخال اسم المدير' }));
      return;
    }
    setErrors(er => ({ ...er, adminName: '' }));
    setSavingName(true);
    const { error } = await supabase
      .from('admin_settings')
      .update({ admin_name: adminName.trim(), updated_at: new Date().toISOString() })
      .eq('id', 1);
    setSavingName(false);
    if (error) {
      onToast('حدث خطأ أثناء الحفظ', 'error');
    } else {
      setNameSaved(true);
      setTimeout(() => setNameSaved(false), 2500);
      onToast('تم حفظ الاسم بنجاح ✓');
    }
  };

  // ── Change Password ──
  const handleChangePassword = async () => {
    const errs: Record<string, string> = {};
    if (!currentPassword) errs.currentPassword = 'يرجى إدخال كلمة المرور الحالية';
    if (!newPassword) errs.newPassword = 'يرجى إدخال كلمة المرور الجديدة';
    if (newPassword.length < 8) errs.newPassword = 'كلمة المرور يجب أن تكون 8 أحرف على الأقل';
    if (newPassword !== confirmPassword) errs.confirmPassword = 'كلمتا المرور غير متطابقتين';
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSavingPassword(true);
    setErrors({});

    const { error: verifyError } = await supabase.auth.signInWithPassword({ email: adminEmail, password: currentPassword });
    if (verifyError) {
      setErrors({ currentPassword: 'كلمة المرور الحالية غير صحيحة' });
      setSavingPassword(false);
      return;
    }

    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/setup-admin-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'change_password', currentEmail: adminEmail, newPassword }),
      });
      const data = await res.json();
      if (data.error) { onToast('حدث خطأ أثناء تغيير كلمة المرور', 'error'); setSavingPassword(false); return; }
      await supabase.auth.signInWithPassword({ email: adminEmail, password: newPassword });
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      setPasswordSaved(true);
      setTimeout(() => setPasswordSaved(false), 2500);
      onToast('تم تغيير كلمة المرور بنجاح ✓');
    } catch {
      onToast('حدث خطأ، تحقق من الاتصال', 'error');
    }
    setSavingPassword(false);
  };

  // ── Email Change: Step 1 — Enter new email ──
  const handleRequestEmailChange = async () => {
    const errs: Record<string, string> = {};
    if (!newEmail.trim()) errs.newEmail = 'يرجى إدخال البريد الإلكتروني الجديد';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) errs.newEmail = 'صيغة البريد الإلكتروني غير صحيحة';
    if (newEmail === adminEmail) errs.newEmail = 'البريد الجديد مطابق للحالي';
    if (!confirmNewEmail.trim()) errs.confirmNewEmail = 'يرجى تأكيد البريد الإلكتروني';
    if (newEmail !== confirmNewEmail) errs.confirmNewEmail = 'البريدان غير متطابقين';
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setSavingEmail(true);

    // Send OTP to the NEW email via Supabase
    const { error } = await supabase.auth.signInWithOtp({
      email: newEmail,
      options: { shouldCreateUser: false },
    });

    // Note: Supabase may return error if email doesn't exist as user — we use updateUser flow instead
    // Use updateUser which sends confirmation to new email
    const { error: updateError } = await supabase.auth.updateUser({ email: newEmail });

    setSavingEmail(false);

    if (updateError) {
      // If error is about email already in use or similar
      if (updateError.message.includes('already')) {
        setErrors({ newEmail: 'هذا البريد الإلكتروني مستخدم بالفعل' });
      } else {
        onToast('حدث خطأ أثناء إرسال رابط التأكيد: ' + updateError.message, 'error');
      }
      return;
    }

    setEmailStep('confirm_sent');
    setEmailCountdown(60);
    onToast('تم إرسال رابط التأكيد إلى بريدك الجديد ✓');
    // Suppress unused error variable
    void error;
  };

  // ── Email Change: Resend ──
  const handleResendConfirmation = async () => {
    if (emailCountdown > 0) return;
    setSavingEmail(true);
    const { error } = await supabase.auth.updateUser({ email: newEmail });
    setSavingEmail(false);
    if (error) {
      onToast('حدث خطأ أثناء إعادة الإرسال', 'error');
    } else {
      setEmailCountdown(60);
      onToast('تم إعادة إرسال رابط التأكيد ✓');
    }
  };

  // ── Email Change: Cancel ──
  const handleCancelEmailChange = () => {
    setEmailStep('idle');
    setNewEmail('');
    setConfirmNewEmail('');
    setOtpCode('');
    setErrors({});
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-2 border-gray-200 border-t-[#1a1a1a] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">

      {/* Auth Status Banner */}
      <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
        <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-lg flex-shrink-0">
          <i className="ri-shield-check-line text-green-600 text-sm"></i>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-green-700 text-xs font-bold">محمي بـ Supabase Auth</p>
          <p className="text-green-600 text-[11px] mt-0.5 truncate" dir="ltr">{adminEmail}</p>
        </div>
        <span className="flex items-center gap-1 text-[10px] font-bold text-green-600 bg-green-100 px-2 py-1 rounded-full whitespace-nowrap">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>نشط
        </span>
      </div>

      {/* ── 1. Admin Name ── */}
      <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
        <h3 className="text-sm font-bold text-[#1a1a1a] mb-4 flex items-center gap-2">
          <div className="w-7 h-7 flex items-center justify-center bg-[#1a1a1a] rounded-lg">
            <i className="ri-user-settings-line text-white text-xs"></i>
          </div>
          اسم المدير
        </h3>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-gray-500">الاسم الظاهر في لوحة التحكم</label>
          <input
            type="text"
            value={adminName}
            onChange={e => { setAdminName(e.target.value); setErrors(er => ({ ...er, adminName: '' })); }}
            placeholder="مثال: مدير المتجر"
            className={`w-full border ${errors.adminName ? 'border-red-300' : 'border-gray-200'} rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors bg-white`}
          />
          {errors.adminName && <p className="text-red-500 text-xs">{errors.adminName}</p>}
        </div>
        <button
          onClick={handleSaveName}
          disabled={savingName}
          className={`mt-4 flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 ${
            nameSaved ? 'bg-green-500 text-white' : 'bg-[#1a1a1a] hover:bg-[#333] text-white'
          }`}
        >
          {savingName ? <i className="ri-loader-4-line animate-spin"></i>
            : nameSaved ? <><i className="ri-checkbox-circle-line"></i> تم الحفظ!</>
            : <><i className="ri-save-line"></i> حفظ الاسم</>}
        </button>
      </div>

      {/* ── 2. Email Change ── */}
      <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
        <h3 className="text-sm font-bold text-[#1a1a1a] mb-4 flex items-center gap-2">
          <div className="w-7 h-7 flex items-center justify-center bg-amber-400 rounded-lg">
            <i className="ri-mail-settings-line text-black text-xs"></i>
          </div>
          البريد الإلكتروني
        </h3>

        {/* Current email display */}
        <div className="flex flex-col gap-1.5 mb-4">
          <label className="text-xs font-medium text-gray-500">البريد الحالي</label>
          <div className="flex items-center gap-2 bg-white border border-gray-100 rounded-xl px-4 py-2.5">
            <i className="ri-mail-line text-gray-300 text-sm flex-shrink-0"></i>
            <span className="text-sm text-gray-500 flex-1" dir="ltr">{adminEmail}</span>
            <span className="text-[10px] font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full whitespace-nowrap">مفعّل</span>
          </div>
        </div>

        {/* Step: idle — show change button */}
        {emailStep === 'idle' && (
          <button
            onClick={() => setEmailStep('enter_new')}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold border border-gray-200 hover:border-[#1a1a1a] hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap text-[#1a1a1a]"
          >
            <i className="ri-mail-settings-line"></i>
            تغيير البريد الإلكتروني
          </button>
        )}

        {/* Step: enter_new — enter new email + confirm */}
        {emailStep === 'enter_new' && (
          <div className="flex flex-col gap-4">
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-2">
              <i className="ri-information-line text-amber-500 text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-amber-700 text-xs leading-relaxed">
                سيتم إرسال رابط تأكيد إلى البريد الجديد. يجب النقر على الرابط لتفعيل التغيير.
              </p>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-gray-500">البريد الإلكتروني الجديد</label>
              <div className="relative">
                <i className="ri-mail-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 text-sm"></i>
                <input
                  type="email"
                  value={newEmail}
                  onChange={e => { setNewEmail(e.target.value); setErrors(er => ({ ...er, newEmail: '' })); }}
                  placeholder="example@domain.com"
                  className={`w-full border ${errors.newEmail ? 'border-red-300' : 'border-gray-200'} rounded-xl pr-9 pl-4 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors bg-white`}
                  dir="ltr"
                />
              </div>
              {errors.newEmail && <p className="text-red-500 text-xs">{errors.newEmail}</p>}
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-gray-500">تأكيد البريد الإلكتروني الجديد</label>
              <div className="relative">
                <i className="ri-mail-check-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 text-sm"></i>
                <input
                  type="email"
                  value={confirmNewEmail}
                  onChange={e => { setConfirmNewEmail(e.target.value); setErrors(er => ({ ...er, confirmNewEmail: '' })); }}
                  placeholder="أعد كتابة البريد الإلكتروني"
                  className={`w-full border ${errors.confirmNewEmail ? 'border-red-300' : 'border-gray-200'} rounded-xl pr-9 pl-4 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors bg-white`}
                  dir="ltr"
                />
              </div>
              {errors.confirmNewEmail && <p className="text-red-500 text-xs">{errors.confirmNewEmail}</p>}
              {confirmNewEmail && newEmail === confirmNewEmail && newEmail && (
                <p className="text-green-500 text-xs flex items-center gap-1">
                  <i className="ri-checkbox-circle-line"></i> البريدان متطابقان
                </p>
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleRequestEmailChange}
                disabled={savingEmail}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold bg-[#1a1a1a] hover:bg-[#333] text-white transition-all cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {savingEmail ? <i className="ri-loader-4-line animate-spin"></i> : <><i className="ri-send-plane-line"></i> إرسال رابط التأكيد</>}
              </button>
              <button
                onClick={handleCancelEmailChange}
                className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-500 border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                إلغاء
              </button>
            </div>
          </div>
        )}

        {/* Step: confirm_sent — waiting for user to click link */}
        {emailStep === 'confirm_sent' && (
          <div className="flex flex-col gap-4">
            <div className="bg-green-50 border border-green-200 rounded-2xl p-5 flex flex-col items-center gap-3 text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-full">
                <i className="ri-mail-send-line text-green-600 text-2xl"></i>
              </div>
              <div>
                <p className="text-green-700 text-sm font-bold">تم إرسال رابط التأكيد!</p>
                <p className="text-green-600 text-xs mt-1">
                  تحقق من بريدك الجديد:
                </p>
                <p className="text-green-700 text-xs font-bold mt-0.5" dir="ltr">{newEmail}</p>
              </div>
              <div className="bg-white rounded-xl px-4 py-3 border border-green-100 w-full text-right">
                <p className="text-gray-600 text-xs font-bold mb-2 flex items-center gap-1.5">
                  <i className="ri-list-check text-green-500"></i>
                  خطوات التأكيد:
                </p>
                <ol className="text-gray-500 text-xs space-y-1.5 list-decimal list-inside">
                  <li>افتح بريدك الإلكتروني الجديد</li>
                  <li>ابحث عن رسالة من Supabase</li>
                  <li>انقر على رابط &quot;Confirm Email Change&quot;</li>
                  <li>سيتم تحديث بريدك تلقائياً</li>
                </ol>
              </div>
            </div>

            <div className="flex items-center justify-between bg-white rounded-xl px-4 py-3 border border-gray-100">
              <p className="text-xs text-gray-500">لم تصلك الرسالة؟</p>
              <button
                onClick={handleResendConfirmation}
                disabled={emailCountdown > 0 || savingEmail}
                className={`text-xs font-bold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 ${
                  emailCountdown > 0 ? 'text-gray-400' : 'text-[#1a1a1a] hover:text-amber-600'
                }`}
              >
                {savingEmail ? (
                  <i className="ri-loader-4-line animate-spin"></i>
                ) : emailCountdown > 0 ? (
                  `إعادة الإرسال بعد ${emailCountdown}ث`
                ) : (
                  <><i className="ri-refresh-line ml-1"></i>إعادة الإرسال</>
                )}
              </button>
            </div>

            <button
              onClick={handleCancelEmailChange}
              className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium text-gray-500 border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-line"></i>
              إلغاء تغيير البريد
            </button>
          </div>
        )}

        {/* Unused OTP state — kept for future use */}
        {emailStep === 'enter_otp' && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-gray-500">رمز التحقق (OTP)</label>
              <input
                type="text"
                value={otpCode}
                onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors bg-white text-center tracking-widest font-mono text-lg"
                dir="ltr"
              />
            </div>
            <button
              onClick={handleCancelEmailChange}
              className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer text-center"
            >
              إلغاء
            </button>
          </div>
        )}
      </div>

      {/* ── 3. Change Password ── */}
      <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
        <h3 className="text-sm font-bold text-[#1a1a1a] mb-4 flex items-center gap-2">
          <div className="w-7 h-7 flex items-center justify-center bg-amber-400 rounded-lg">
            <i className="ri-lock-password-line text-black text-xs"></i>
          </div>
          تغيير كلمة المرور
        </h3>

        <div className="flex flex-col gap-4">
          {/* Current Password */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-gray-500">كلمة المرور الحالية</label>
            <div className="relative">
              <input
                type={showCurrent ? 'text' : 'password'}
                value={currentPassword}
                onChange={e => { setCurrentPassword(e.target.value); setErrors(er => ({ ...er, currentPassword: '' })); }}
                placeholder="••••••••"
                className={`w-full border ${errors.currentPassword ? 'border-red-300' : 'border-gray-200'} rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors bg-white pl-10`}
              />
              <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute inset-y-0 left-3 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className={`text-sm ${showCurrent ? 'ri-eye-off-line' : 'ri-eye-line'}`}></i>
              </button>
            </div>
            {errors.currentPassword && <p className="text-red-500 text-xs">{errors.currentPassword}</p>}
          </div>

          {/* New Password */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-gray-500">كلمة المرور الجديدة (8 أحرف على الأقل)</label>
            <div className="relative">
              <input
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={e => { setNewPassword(e.target.value); setErrors(er => ({ ...er, newPassword: '' })); }}
                placeholder="••••••••"
                className={`w-full border ${errors.newPassword ? 'border-red-300' : 'border-gray-200'} rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors bg-white pl-10`}
              />
              <button type="button" onClick={() => setShowNew(!showNew)} className="absolute inset-y-0 left-3 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className={`text-sm ${showNew ? 'ri-eye-off-line' : 'ri-eye-line'}`}></i>
              </button>
            </div>
            {errors.newPassword && <p className="text-red-500 text-xs">{errors.newPassword}</p>}
            {newPassword && (
              <div className="flex items-center gap-1.5 mt-1">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${
                    newPassword.length >= i * 3
                      ? newPassword.length >= 12 ? 'bg-green-500' : newPassword.length >= 8 ? 'bg-amber-400' : 'bg-red-400'
                      : 'bg-gray-200'
                  }`}></div>
                ))}
                <span className="text-[10px] text-gray-400 whitespace-nowrap">
                  {newPassword.length >= 12 ? 'قوية جداً' : newPassword.length >= 8 ? 'جيدة' : 'ضعيفة'}
                </span>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-gray-500">تأكيد كلمة المرور الجديدة</label>
            <div className="relative">
              <input
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={e => { setConfirmPassword(e.target.value); setErrors(er => ({ ...er, confirmPassword: '' })); }}
                placeholder="أعد كتابة كلمة المرور"
                className={`w-full border ${errors.confirmPassword ? 'border-red-300' : 'border-gray-200'} rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors bg-white pl-10`}
              />
              <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute inset-y-0 left-3 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className={`text-sm ${showConfirm ? 'ri-eye-off-line' : 'ri-eye-line'}`}></i>
              </button>
            </div>
            {errors.confirmPassword && <p className="text-red-500 text-xs">{errors.confirmPassword}</p>}
            {confirmPassword && newPassword === confirmPassword && (
              <p className="text-green-500 text-xs flex items-center gap-1">
                <i className="ri-checkbox-circle-line"></i> كلمتا المرور متطابقتان
              </p>
            )}
          </div>
        </div>

        <button
          onClick={handleChangePassword}
          disabled={savingPassword}
          className={`mt-4 flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 ${
            passwordSaved ? 'bg-green-500 text-white' : 'bg-amber-400 hover:bg-amber-500 text-black'
          }`}
        >
          {savingPassword ? <i className="ri-loader-4-line animate-spin"></i>
            : passwordSaved ? <><i className="ri-checkbox-circle-line"></i> تم التغيير!</>
            : <><i className="ri-lock-password-line"></i> تغيير كلمة المرور</>}
        </button>
      </div>

      {/* Security Info */}
      <div className="bg-gray-50 rounded-xl px-4 py-4 border border-gray-100 flex flex-col gap-3">
        <p className="text-xs font-bold text-gray-600 flex items-center gap-2">
          <i className="ri-shield-keyhole-line text-[#1a1a1a]"></i>
          معلومات الأمان
        </p>
        <div className="flex flex-col gap-2">
          {[
            { icon: 'ri-lock-2-line', text: 'كلمة المرور مشفّرة بـ bcrypt في Supabase Auth' },
            { icon: 'ri-mail-check-line', text: 'تغيير البريد يتطلب تأكيداً عبر رابط يُرسل للبريد الجديد' },
            { icon: 'ri-time-line', text: 'الجلسة تنتهي تلقائياً عند الخروج أو انتهاء الصلاحية' },
            { icon: 'ri-shield-check-line', text: 'التحقق يتم عبر Supabase Auth المشفّر بالكامل' },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className={`${item.icon} text-green-500 text-xs`}></i>
              </div>
              <p className="text-gray-500 text-xs">{item.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
